import React from 'react';
import ThemeProps from '../../Theme/ThemeProps';
import {
  Spacer,
  Box,
  Text,
  CheckCircleIcon,
  InfoIcon,
  CrossCircleIcon,
  CrossIcon,
  theme,
  useFela,
} from '../../index';
import { BannerProps } from './BannerProps';
import { IconProps } from '../../Atoms/Icon/icon';
import { TextProps } from '../../Atoms/Text/Text';

type InnerElement = 'div' | 'a';

const containerStyle = ({
  theme,
  kind,
}: {
  theme: ThemeProps;
  kind: BannerProps['kind'];
}) => ({
  width: '100%',
  flexDirection: 'row',
  /**
   * TODO:
   * - Use corresponding theme value when available: theme.radius.s.rem
   */
  borderRadius: '0.25rem',
  backgroundColor: theme.palette.color5.hex,
  extend: [
    {
      condition: kind === 'neutral',
      style: {
        /**
         * TODO:
         * - Use corresponding theme value when available: theme.palette.backgroundInfo.hex
         */
        backgroundColor: '#E5E9F1',
      },
    },
    {
      condition: kind === 'positive',
      style: {
        /**
         * TODO:
         * - Use corresponding theme value when available: theme.palette.backgroundSuccess.hex
         */
        backgroundColor: '#EDF7E5',
      },
    },
    {
      condition: kind === 'warning',
      style: {
        /**
         * TODO:
         * - Use corresponding theme value when available: theme.palette.backgroundWarning.hex
         */
        backgroundColor: '#FDF6E9',
      },
    },
    {
      condition: kind === 'error',
      style: {
        /**
         * TODO:
         * - Use corresponding theme value when available: theme.palette.backgroundError.hex
         */
        backgroundColor: '#FDF2F2',
      },
    },
  ],
});

const contentStyle = ({ theme }: { theme: ThemeProps }) => ({
  flex: '1 1 auto',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  flexWrap: 'wrap',
  padding: `${theme.dimension.spacing.xxs.rem} 0 ${theme.dimension.spacing.xxs.rem} 0`,
});

const contentInnerStyle = () => ({
  paddingTop: 3,
});

const iconStyle = ({ theme }: { theme: ThemeProps }) => ({
  flex: `0 0 ${theme.dimension.spacing.m.rem}`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: `${theme.dimension.spacing.xxs.rem} 0 ${theme.dimension.spacing.xxs.rem} 0`,
});

const actionStyle = () => ({
  boxSizing: 'border-box',
  flex: '0 0 auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  marginLeft: 'auto',
});

const closeButtonStyle = ({ theme }: { theme: ThemeProps }) => ({
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: `${theme.dimension.spacing.xs.rem} ${theme.dimension.spacing.xxs.rem}`,
  height: 'auto',
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  userSelect: 'none',
  appearance: 'none',
  lineHeight: 1,
});

const innerStyle = ({ As }: { As: InnerElement }) => ({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'row',
  extend: [
    {
      condition: As === 'a',
      style: {
        textDecoration: 'none',
      },
    },
  ],
});

const iconMapping = {
  positive: CheckCircleIcon,
  neutral: InfoIcon,
  warning: ({ ...props }) => (
    <InfoIcon color={theme.palette.color7.hex} {...props} />
  ),
  error: CrossCircleIcon,
};

export const BannerIcon = ({
  dmId = 'dm-banner-icon',
  id,
  icon,
  kind,
  style,
}: {
  dmId?: BannerProps['dmId'];
  id?: BannerProps['id']; // todo: dmId should be required (myt-7757)
  icon?: (props: IconProps) => JSX.Element;
  kind: BannerProps['kind'];
  style?: object;
}) => {
  const { css } = useFela();
  const Icon = icon || iconMapping[kind];

  return (
    <div data-designsystem data-dmid={dmId} className={css([iconStyle, style])}>
      {Icon && <Icon id={`${id}-icon`} dmId={`${dmId}-icon`} />}
    </div>
  );
};
export const BannerAction = ({
  dmId = 'dm-banner-action',
  id,
  onClose,
  style,
}: {
  id?: BannerProps['id']; // todo: dmId should be required (myt-7757)
  dmId?: BannerProps['dmId'];
  onClose?: BannerProps['onClose'];
  style?: object;
}) => {
  const { css } = useFela();

  return (
    <div
      data-designsystem
      data-dmid={dmId}
      className={css([actionStyle, style])}>
      <button
        data-dmid={`${dmId}-button`}
        onClick={onClose}
        className={css(closeButtonStyle)}>
        <CrossIcon
          id={`${id}-close-icon`}
          dmId={`${dmId}-icon`}
          color={theme.palette.color3.hex}
          size={14}
          style={{ display: 'block' }}
        />
      </button>
    </div>
  );
};

export const BannerContent = ({
  dmId,
  children,
  style,
}: {
  dmId: BannerProps['dmId'];
  children: any;
  style?: object;
}) => {
  const { css } = useFela();

  return (
    <div
      data-designsystem
      data-dmid={dmId}
      className={css([contentStyle, style])}>
      <div data-dmid={`${dmId}-inner`} className={css(contentInnerStyle)}>
        {children}
      </div>
    </div>
  );
};

export const BannerText = ({
  dmId,
  children,
  style,
  ...otherProps
}: TextProps) => (
  <Text dmId={dmId} style={style} {...otherProps}>
    {children}
  </Text>
);

export const BannerWrapper = ({
  dmId,
  children,
  kind,
  style,
  ...otherProps
}: {
  dmId: BannerProps['dmId'];
  children: React.ReactNode[];
  kind: BannerProps['kind'];
  style?: object;
}) => (
  <Box
    data-designsystem
    dmId={dmId}
    style={[
      ({ theme }: { theme: ThemeProps }) => containerStyle({ theme, kind }),
      style,
    ]}
    {...otherProps}>
    {children}
  </Box>
);

export const BannerInner = ({
  dmId,
  as = 'div',
  children,
  style,
  ...otherProps
}: {
  dmId: BannerProps['dmId'];
  as?: InnerElement;
  children: any;
  style?: any;
  [key: string]: any;
}) => {
  const Component = as;
  const { css } = useFela({ Component });

  return (
    <Component
      data-designsystem
      data-dmid={dmId}
      className={css([innerStyle, style])}
      {...otherProps}>
      {children}
    </Component>
  );
};

function Banner({
  dmId,
  kind = 'neutral',
  icon,
  id,
  children,
  styles,
  onClose,
  ...otherProps
}: BannerProps) {
  return (
    <BannerWrapper
      dmId={dmId}
      kind={kind}
      style={styles?.wrapper}
      {...otherProps}>
      <BannerInner dmId={`${dmId}-inner`}>
        <Spacer size={theme.dimension.spacing.xxs.rem} />
        <BannerIcon
          id={`${dmId}-icon`}
          dmId={`${dmId}-icon-wrapper`}
          icon={icon}
          kind={kind}
          style={styles?.icon}
        />
        <Spacer size={theme.dimension.spacing.xxxs.rem} />
        <BannerContent dmId={`${dmId}-content`} style={styles?.content}>
          <BannerText dmId={`${dmId}-text`} style={styles?.text}>
            {children}
          </BannerText>
        </BannerContent>
        {children && !onClose && (
          <Spacer size={theme.dimension.spacing.xxs.rem} />
        )}
      </BannerInner>
      {onClose && (
        <BannerAction
          id={`${id || dmId}-action`} // todo: dmid should be removed as soon as id is required (myt-7757)
          dmId={`${dmId}-action`}
          onClose={onClose}
        />
      )}
    </BannerWrapper>
  );
}

export default Banner;
